import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faSave, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import './styles/ContactsModal.css';

const ContactsModal = ({ isOpen, onClose }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newContact, setNewContact] = useState({
    client_name: '',
    phone_number: '',
    normalized_phone: '',
    group_chat_id: '',
    message_location: 'iMessage',
    auto_messaging_enabled: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // Fetch from contact_list collection
      const response = await axios.get(API_ENDPOINTS.MESSAGING.GET_CONTACTS);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.client_name || !newContact.phone_number) {
      toast.error('Name and phone number are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(API_ENDPOINTS.MESSAGING.ADD_CONTACT, {
        ...newContact,
        client_id: `custom_${Date.now()}` // Generate custom ID for manual entries
      });
      
      toast.success('Contact added successfully');
      setNewContact({
        client_name: '',
        phone_number: '',
        normalized_phone: '',
        group_chat_id: '',
        message_location: 'iMessage',
        auto_messaging_enabled: true
      });
      fetchContacts();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContact = async (contactId, updatedData) => {
    setLoading(true);
    try {
      await axios.put(API_ENDPOINTS.MESSAGING.UPDATE_CONTACT(contactId), updatedData);
      toast.success('Contact updated successfully');
      setEditingId(null);
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(API_ENDPOINTS.MESSAGING.DELETE_CONTACT(contactId));
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="contacts-modal" onClick={e => e.stopPropagation()}>
        <div className="contacts-modal-header">
          <h2>Manage Contacts & Group Chats</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="contacts-modal-content">
          {/* Add New Contact Form */}
          <div className="add-contact-section">
            <h3>Add New Contact</h3>
            <div className="contact-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Contact Name *"
                  value={newContact.client_name}
                  onChange={(e) => setNewContact({ ...newContact, client_name: e.target.value })}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Phone Number *"
                  value={newContact.phone_number}
                  onChange={(e) => setNewContact({ ...newContact, phone_number: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="E.164 Format (optional)"
                  value={newContact.normalized_phone}
                  onChange={(e) => setNewContact({ ...newContact, normalized_phone: e.target.value })}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Group Chat ID (optional)"
                  value={newContact.group_chat_id}
                  onChange={(e) => setNewContact({ ...newContact, group_chat_id: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={newContact.message_location}
                  onChange={(e) => setNewContact({ ...newContact, message_location: e.target.value })}
                  className="form-select"
                >
                  <option value="iMessage">iMessage</option>
                  <option value="SMS">SMS</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Telegram">Telegram</option>
                </select>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newContact.auto_messaging_enabled}
                    onChange={(e) => setNewContact({ ...newContact, auto_messaging_enabled: e.target.checked })}
                  />
                  <span>Enable Auto-Messaging</span>
                </label>
              </div>
              <button 
                className="add-button"
                onClick={handleAddContact}
                disabled={loading}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faPlus} spin={loading} />
                {' '}Add Contact
              </button>
            </div>
          </div>

          {/* Contacts List */}
          <div className="contacts-list-section">
            <h3>All Contacts ({contacts.length})</h3>
            {loading && contacts.length === 0 ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading contacts...</span>
              </div>
            ) : contacts.length === 0 ? (
              <div className="empty-state">
                <p>No contacts found. Add your first contact above.</p>
              </div>
            ) : (
              <div className="contacts-table-wrapper">
                <table className="contacts-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>E.164</th>
                      <th>Group Chat ID</th>
                      <th>Platform</th>
                      <th>Auto-Msg</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.client_id || contact._id}>
                        <td>{contact.client_name || 'Unknown'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{contact.phone_number || '-'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {contact.normalized_phone || '-'}
                        </td>
                        <td style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '11px',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {contact.group_chat_id || '-'}
                        </td>
                        <td>
                          <span className={`platform-badge ${contact.message_location?.toLowerCase() || 'imessage'}`}>
                            {contact.message_location || 'iMessage'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${contact.auto_messaging_enabled !== false ? 'enabled' : 'disabled'}`}>
                            {contact.auto_messaging_enabled !== false ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteContact(contact.client_id || contact._id)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsModal;

